import {
	Button,
	Card,
	DatePicker,
	InputNumber,
	Modal,
	Popconfirm,
	Space,
	Switch,
	Table,
	Tag,
	Typography,
	message,
} from 'antd'
import { CommentOutlined } from '@ant-design/icons'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import './ChatEngagement.scss'

const { Text, Paragraph } = Typography

const POSTED_HISTORY_LIMIT = 20

const formatInTz = (utc, timeZone) => {
	try {
		return new Intl.DateTimeFormat('en-CA', {
			timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		}).format(new Date(utc))
	} catch {
		return '—'
	}
}

const formatDualClock = utc => {
	if (!utc) return '—'
	const d = new Date(utc)
	if (Number.isNaN(d.getTime())) return '—'
	return `${formatInTz(d, 'Asia/Ho_Chi_Minh')} ICT · ${formatInTz(d, 'America/New_York')} ET`
}

const statusTagColor = status => {
	switch (status) {
		case 'pending':
			return 'gold'
		case 'approved':
			return 'blue'
		case 'partial':
			return 'purple'
		case 'posted':
			return 'green'
		case 'rejected':
			return 'red'
		case 'cancelled':
			return 'default'
		default:
			return 'default'
	}
}

const enrichThread = thread => {
	const drafts = thread.drafts || []
	let pendingCount = 0
	let approvedCount = 0
	let postedCount = 0
	drafts.forEach(d => {
		if (d.status === 'pending') pendingCount += 1
		else if (d.status === 'approved') approvedCount += 1
		else if (d.status === 'posted') postedCount += 1
	})
	const total = drafts.length || thread.turnCount || 0
	let displaySummary = thread.statusSummary || 'unknown'
	if (postedCount > 0 && approvedCount > 0) displaySummary = 'partial'
	else if (postedCount > 0 && pendingCount === 0 && approvedCount === 0) {
		displaySummary = 'posted'
	} else if (approvedCount > 0) displaySummary = 'approved'
	else if (pendingCount > 0) displaySummary = 'pending'

	return {
		...thread,
		pendingCount,
		approvedCount,
		postedCount,
		progressLabel: total ? `${postedCount}/${total} posted` : null,
		displaySummary,
	}
}

const QC_READY_MIN = 80

const parseQCFlags = flags => {
	if (Array.isArray(flags)) return flags
	if (typeof flags !== 'string' || !flags.trim()) return []
	try {
		const parsed = JSON.parse(flags)
		return Array.isArray(parsed) ? parsed : []
	} catch {
		return []
	}
}

const hasHeavyQCFlags = flags =>
	(flags || []).some(f => ['salesy', 'off_topic', 'too_formal', 'repetitive'].includes(f))

const reviewLaneOf = thread => {
	if (thread.reviewLane === 'ready' || thread.reviewLane === 'needs_look') {
		return thread.reviewLane
	}
	const score = Number(thread.qcScore) || 0
	const flags = parseQCFlags(thread.qcFlags)
	if (score >= QC_READY_MIN && !hasHeavyQCFlags(flags)) return 'ready'
	// Legacy drafts without QC metadata → needs a human look.
	if (!thread.topic && !score) return 'needs_look'
	return 'needs_look'
}

const partitionThreads = threads => {
	const pendingReady = []
	const pendingNeedsLook = []
	const approved = []
	const posted = []
	;(threads || []).forEach(raw => {
		const thread = enrichThread(raw)
		// Pending review: still has pending beats — split Ready vs Needs look.
		if (thread.pendingCount > 0) {
			const lane = reviewLaneOf(thread)
			if (lane === 'ready') pendingReady.push(thread)
			else pendingNeedsLook.push(thread)
		}
		// Waiting: zero posted beats yet (all remaining are approved / not started).
		// Partials (≥1 posted) go only to Posted history — no duplicate cards.
		if (thread.approvedCount > 0 && thread.postedCount === 0) {
			approved.push(thread)
		}
		// History: anything with ≥1 posted beat (partial or complete).
		if (thread.postedCount > 0) posted.push(thread)
	})
	const byEarliestAsc = (a, b) =>
		new Date(a.earliestAt).getTime() - new Date(b.earliestAt).getTime()
	const byEarliestDesc = (a, b) =>
		new Date(b.earliestAt).getTime() - new Date(a.earliestAt).getTime()
	const byQcDescThenEarliest = (a, b) => {
		const scoreDiff = (Number(b.qcScore) || 0) - (Number(a.qcScore) || 0)
		if (scoreDiff !== 0) return scoreDiff
		return byEarliestAsc(a, b)
	}
	pendingReady.sort(byQcDescThenEarliest)
	pendingNeedsLook.sort(byEarliestAsc)
	approved.sort(byEarliestAsc)
	posted.sort(byEarliestDesc)
	return {
		pendingReady,
		pendingNeedsLook,
		approved,
		posted: posted.slice(0, POSTED_HISTORY_LIMIT),
	}
}

const ThreadList = ({
	threads,
	emptyText,
	showApproveReject,
	allowEdit,
	onApprove,
	onReject,
	onEdit,
}) => {
	if (!threads.length) {
		return <div className="ce-empty">{emptyText}</div>
	}
	return threads.map(thread => (
		<div className="ce-thread-card" key={thread.conversationId}>
			<div className="ce-thread-meta">
				<Tag color={statusTagColor(thread.displaySummary || thread.statusSummary)}>
					{(thread.displaySummary || thread.statusSummary || 'unknown').toUpperCase()}
				</Tag>
				{thread.progressLabel ? (
					<Tag color={thread.postedCount > 0 && thread.approvedCount > 0 ? 'purple' : 'green'}>
						{thread.progressLabel}
					</Tag>
				) : null}
				{thread.topic ? <Tag color="cyan">{thread.topic}</Tag> : null}
				{thread.tone ? <Tag color="geekblue">{thread.tone}</Tag> : null}
				{Number(thread.qcScore) > 0 ? (
					<Tag color={reviewLaneOf(thread) === 'ready' ? 'green' : 'orange'}>
						QC {thread.qcScore}
						{(() => {
							const flags = parseQCFlags(thread.qcFlags).filter(f => f && f !== 'ok')
							return flags.length ? ` · ${flags.join(', ')}` : ''
						})()}
					</Tag>
				) : null}
				<Tag>{thread.turnCount}-turn</Tag>
				<Tag color="blue">{thread.windowLabel || 'window'}</Tag>
				<span>Earliest: {formatDualClock(thread.earliestAt)}</span>
				<span>
					<Text code>{thread.conversationId}</Text>
				</span>
			</div>
			{(thread.drafts || []).map((draft, idx) => (
				<div key={draft.id} className={`ce-turn ${idx > 0 ? 'is-reply' : ''}`}>
					<div className="ce-turn-head">
						<strong>
							{idx > 0 ? '↳ ' : ''}
							{draft.seedFirstName} {draft.seedLastName}
						</strong>
						<Tag color={statusTagColor(draft.status)}>{draft.status}</Tag>
						<span>{(draft.localeHint || '').toUpperCase()}</span>
						<span>
							{draft.status === 'posted' ? 'Sent (scheduled): ' : 'Scheduled: '}
							{formatDualClock(draft.scheduledAt)}
						</span>
						{allowEdit && (draft.status === 'pending' || draft.status === 'approved') ? (
							<Button type="link" size="small" onClick={() => onEdit(draft)}>
								Edit text / time
							</Button>
						) : null}
					</div>
					<div className="ce-turn-message">{draft.message}</div>
				</div>
			))}
			{showApproveReject ? (
				<div className="ce-actions">
					<Button type="primary" onClick={() => onApprove(thread.conversationId)}>
						Approve thread
					</Button>
					<Popconfirm
						title="Reject entire thread?"
						onConfirm={() => onReject(thread.conversationId)}
					>
						<Button danger>Reject thread</Button>
					</Popconfirm>
				</div>
			) : null}
		</div>
	))
}

export const ChatEngagement = () => {
	const [settings, setSettings] = useState(null)
	const [threads, setThreads] = useState([])
	const [personas, setPersonas] = useState([])
	const [metrics, setMetrics] = useState([])
	const [loading, setLoading] = useState(false)
	const [savingCap, setSavingCap] = useState(false)
	const [dailyCapDraft, setDailyCapDraft] = useState(6)
	const [editDraft, setEditDraft] = useState(null)
	const [editMessage, setEditMessage] = useState('')
	const [editScheduledAt, setEditScheduledAt] = useState(null)
	const [editSaving, setEditSaving] = useState(false)
	const editSavingRef = useRef(false)

	const { pendingReady, pendingNeedsLook, approved, posted } = useMemo(
		() => partitionThreads(threads),
		[threads],
	)
	const pendingTotal = pendingReady.length + pendingNeedsLook.length

	const refreshAll = useCallback(async () => {
		setLoading(true)
		try {
			const [settingsRs, threadsRs, personasRs, metricsRs] = await Promise.all([
				api.chatSeedAdminApi.getSettings(),
				// Load all statuses so approved/posted don't vanish after review.
				api.chatSeedAdminApi.getThreads({ status: 'all' }),
				api.chatSeedAdminApi.getPersonas(),
				api.chatSeedAdminApi.getDailyMetrics({ tz: 'Asia/Ho_Chi_Minh' }),
			])
			setSettings(settingsRs || null)
			setDailyCapDraft(settingsRs?.dailyCap ?? 6)
			setThreads(Array.isArray(threadsRs?.data) ? threadsRs.data : [])
			setPersonas(Array.isArray(personasRs?.data) ? personasRs.data : [])
			setMetrics(Array.isArray(metricsRs?.data) ? metricsRs.data : [])
		} catch (err) {
			message.error(err?.message || 'Failed to load chat engagement data')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		refreshAll()
	}, [refreshAll])

	const handleToggleEnabled = async checked => {
		const apply = async () => {
			try {
				const rs = await api.chatSeedAdminApi.updateSettings({ enabled: checked })
				setSettings(rs)
				message.success(checked ? 'Seeded chat ON' : 'Seeded chat OFF')
				await refreshAll()
			} catch (err) {
				message.error(err?.message || 'Could not update kill-switch')
			}
		}

		if (checked) {
			Modal.confirm({
				title: 'Turn seeded chat ON?',
				content:
					'Approved drafts may post to the public lobby within VN/US windows under the daily cap. Review the pending queue first.',
				okText: 'Turn ON',
				onOk: apply,
			})
			return
		}
		await apply()
	}

	const handleSaveCap = async () => {
		const cap = Number(dailyCapDraft)
		if (cap < 2 || cap > 10) {
			message.error('Daily cap must be between 2 and 10')
			return
		}
		setSavingCap(true)
		try {
			const rs = await api.chatSeedAdminApi.updateSettings({ dailyCap: cap })
			setSettings(rs)
			message.success('Daily cap updated')
		} catch (err) {
			message.error(err?.message || 'Could not update daily cap')
		} finally {
			setSavingCap(false)
		}
	}

	const handleBootstrap = async () => {
		try {
			const rs = await api.chatSeedAdminApi.bootstrapPersonas()
			message.success(
				`Personas ready (created ${rs?.created ?? 0}, skipped ${rs?.skipped ?? 0})`
			)
			await refreshAll()
		} catch (err) {
			message.error(err?.message || 'Bootstrap failed')
		}
	}

	const handleRefreshPersonas = async () => {
		try {
			const rs = await api.chatSeedAdminApi.refreshPersonas()
			const missing = Array.isArray(rs?.missing) ? rs.missing : []
			message.success(
				`Refreshed ${rs?.updated ?? 0} persona profile(s)` +
					(missing.length ? ` (missing: ${missing.join(', ')})` : '')
			)
			await refreshAll()
		} catch (err) {
			message.error(err?.message || 'Refresh personas failed')
		}
	}

	const handleGenerate = async () => {
		try {
			const rs = await api.chatSeedAdminApi.generateDrafts()
			message.success(`Generated ${rs?.threadsCreated ?? 0} thread(s)`)
			await refreshAll()
		} catch (err) {
			message.error(err?.message || 'Draft generation failed')
		}
	}

	const handleApproveThread = async conversationId => {
		try {
			await api.chatSeedAdminApi.approveThread(conversationId)
			message.success('Thread approved — see Approved (waiting to post)')
			await refreshAll()
		} catch (err) {
			message.error(err?.message || 'Approve failed')
		}
	}

	const handleRejectThread = async conversationId => {
		try {
			await api.chatSeedAdminApi.rejectThread(conversationId)
			message.success('Thread rejected')
			await refreshAll()
		} catch (err) {
			message.error(err?.message || 'Reject failed')
		}
	}

	const openEdit = draft => {
		setEditDraft(draft)
		setEditMessage(draft?.message || '')
		setEditScheduledAt(draft?.scheduledAt ? moment(draft.scheduledAt) : null)
	}

	const closeEdit = () => {
		setEditDraft(null)
		setEditMessage('')
		setEditScheduledAt(null)
		setEditSaving(false)
		editSavingRef.current = false
	}

	const saveEdit = async () => {
		// Ant Design Modal can fire onOk twice (OK click + Enter). Guard so we
		// only toast / PUT once.
		if (!editDraft || editSavingRef.current) return
		const msg = editMessage.trim()
		if (!msg) {
			message.error('Message must not be empty')
			return Promise.reject(new Error('empty message'))
		}
		if (!editScheduledAt || !editScheduledAt.isValid()) {
			message.error('Scheduled time is required')
			return Promise.reject(new Error('invalid schedule'))
		}
		editSavingRef.current = true
		setEditSaving(true)
		try {
			await api.chatSeedAdminApi.editDraft(editDraft.id, {
				message: msg,
				scheduledAt: editScheduledAt.toDate().toISOString(),
			})
			message.success('Draft updated (text + schedule)')
			closeEdit()
			await refreshAll()
		} catch (err) {
			editSavingRef.current = false
			setEditSaving(false)
			message.error(err?.message || 'Edit failed')
			return Promise.reject(err)
		}
	}

	const formatMetricDayIct = day => {
		// API returns YYYY-MM-DD (ICT calendar day). Show start-of-day datetime in ICT.
		if (!day || typeof day !== 'string') return '—'
		const m = moment(day, 'YYYY-MM-DD', true)
		if (!m.isValid()) return String(day)
		return `${m.format('YYYY-MM-DD')} 00:00 ICT`
	}

	const metricColumns = [
		{
			title: 'Day (ICT)',
			dataIndex: 'day',
			key: 'day',
			render: day => formatMetricDayIct(day),
		},
		{ title: 'Seed msgs', dataIndex: 'seedMessages', key: 'seedMessages' },
		{ title: 'Human msgs', dataIndex: 'humanMessages', key: 'humanMessages' },
		{ title: 'Seed senders', dataIndex: 'seedSenders', key: 'seedSenders' },
		{ title: 'Human senders', dataIndex: 'humanSenders', key: 'humanSenders' },
	]

	const personaColumns = [
		{ title: 'ID', dataIndex: 'userId', key: 'userId', width: 80 },
		{
			title: 'Avatar',
			dataIndex: 'avatarUrl',
			key: 'avatarUrl',
			width: 64,
			render: url =>
				url ? (
					<img
						src={url}
						alt=""
						style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
					/>
				) : (
					'—'
				),
		},
		{ title: 'Auth name', dataIndex: 'authName', key: 'authName' },
		{
			title: 'Display',
			key: 'display',
			render: (_, r) => `${r.lastName || ''} ${r.firstName || ''}`.trim(),
		},
		{ title: 'Region', dataIndex: 'region', key: 'region', width: 90 },
		{
			title: 'Bio',
			dataIndex: 'bio',
			key: 'bio',
			ellipsis: true,
		},
	]

	return (
		<ListPageShell className="chat-engagement">
			<ListPageIntro
				icon={CommentOutlined}
				title="Chat engagement"
				subtitle="Seeded lobby chatter: kill-switch, approval queue, and daily seed vs human metrics. Default OFF — nothing posts until you enable and approve."
			/>

			<Card className="ce-section" loading={loading} title="Kill-switch & daily cap">
				<div className="ce-status-row">
					<Space>
						<span>Seeded chat</span>
						<Switch
							checked={!!settings?.enabled}
							onChange={handleToggleEnabled}
							checkedChildren="ON"
							unCheckedChildren="OFF"
						/>
					</Space>
					<Space>
						<span>Daily cap (2–10)</span>
						<InputNumber
							min={2}
							max={10}
							value={dailyCapDraft}
							onChange={v => setDailyCapDraft(v)}
						/>
						<Button loading={savingCap} onClick={handleSaveCap}>
							Save cap
						</Button>
					</Space>
				</div>
				<div className="ce-status-row">
					<span className="ce-stat">
						Pending: <strong>{settings?.pendingCount ?? pendingTotal}</strong>
					</span>
					<span className="ce-stat">
						Approved waiting: <strong>{settings?.approvedWaiting ?? approved.length}</strong>
					</span>
					<span className="ce-stat">
						Seed today: <strong>{settings?.postedToday ?? 0}</strong>
					</span>
					<span className="ce-stat">
						Human today: <strong>{settings?.humanToday ?? 0}</strong>
					</span>
					<span className="ce-stat">
						Last updated by: <strong>{settings?.updatedBy || '—'}</strong>
					</span>
				</div>
				<Paragraph type="secondary" style={{ marginBottom: 0 }}>
					Turning OFF cancels approved-not-yet-posted drafts so a backlog cannot flush
					when you turn ON again. Existing lobby messages stay. Beats post one-by-one when
					due (e.g. A at 14:20, B at 20:20) — a thread can be <strong>partial</strong>{' '}
					(1/2 posted) until the later beat fires.
				</Paragraph>
			</Card>

			<Card className="ce-section" title="Daily seed vs human (ICT)" loading={loading}>
				<Paragraph type="secondary">
					Counts per ICT calendar day. Day column shows start-of-day as{' '}
					<code>YYYY-MM-DD HH:mm ICT</code>.
				</Paragraph>
				<Table
					rowKey="day"
					size="small"
					pagination={false}
					columns={metricColumns}
					dataSource={metrics}
					loading={loading}
				/>
			</Card>

			<Card
				className="ce-section"
				title={`Pending review — Ready (${pendingReady.length})`}
				extra={
					<Space>
						<span style={{ color: 'rgba(0,0,0,0.45)' }}>
							{pendingTotal} pending total
						</span>
						<Button onClick={refreshAll}>Refresh</Button>
						<Button type="primary" onClick={handleGenerate} disabled={!settings?.enabled}>
							Generate drafts
						</Button>
					</Space>
				}
				loading={loading}
			>
				<Paragraph type="secondary">
					QC ≥ {QC_READY_MIN}, no heavy flags — scan quickly then Approve. Content v2 uses
					topic/tone + few-shot from approved threads.
				</Paragraph>
				<ThreadList
					threads={pendingReady}
					emptyText="No Ready threads. Generate drafts or check Needs look below."
					showApproveReject
					allowEdit
					onApprove={handleApproveThread}
					onReject={handleRejectThread}
					onEdit={openEdit}
				/>
			</Card>

			<Card
				className="ce-section"
				title={`Pending review — Needs look (${pendingNeedsLook.length})`}
				loading={loading}
			>
				<Paragraph type="secondary">
					Lower QC score or flags (salesy / off_topic / too_formal / repetitive). Edit or
					Reject before approving.
				</Paragraph>
				<ThreadList
					threads={pendingNeedsLook}
					emptyText="Nothing needs a closer look."
					showApproveReject
					allowEdit
					onApprove={handleApproveThread}
					onReject={handleRejectThread}
					onEdit={openEdit}
				/>
			</Card>

			<Card
				className="ce-section"
				title={`Approved — waiting to post (${approved.length})`}
				loading={loading}
			>
				<Paragraph type="secondary">
					Threads with <strong>no beats posted yet</strong> (all still approved /
					scheduled). Once any beat posts, the thread moves to Posted history only — no
					duplicates. Times in ICT + ET.
				</Paragraph>
				<ThreadList
					threads={approved}
					emptyText="No approved threads waiting. Approve a pending thread to see it here."
					showApproveReject={false}
					allowEdit
					onEdit={openEdit}
				/>
			</Card>

			<Card
				className="ce-section"
				title={`Posted / recent history (last ${POSTED_HISTORY_LIMIT})`}
				loading={loading}
			>
				<Paragraph type="secondary">
					Threads with ≥1 beat already in the lobby (partials show e.g. 1/2 posted). Edit
					remaining approved beats on this card. Fully done threads stay here only.
				</Paragraph>
				<ThreadList
					threads={posted}
					emptyText="No posted seed threads yet."
					showApproveReject={false}
					allowEdit
					onEdit={openEdit}
				/>
			</Card>

			<Card
				className="ce-section"
				title="Seed personas"
				extra={
					<Space>
						<Popconfirm
							title="Refresh display names, bios, and avatars for existing seed users? User IDs stay the same."
							onConfirm={handleRefreshPersonas}
						>
							<Button type="primary">Refresh personas</Button>
						</Popconfirm>
						<Button onClick={handleBootstrap}>Bootstrap 10 seed users</Button>
					</Space>
				}
			>
				<Table
					rowKey="userId"
					size="small"
					pagination={false}
					columns={personaColumns}
					dataSource={personas}
					loading={loading}
				/>
			</Card>

			<Modal
				title={`Edit draft (${editDraft?.status || ''})`}
				visible={!!editDraft}
				onCancel={closeEdit}
				onOk={() => saveEdit()}
				okText="Save"
				okButtonProps={{ loading: editSaving, disabled: editSaving }}
				confirmLoading={editSaving}
				width={560}
				destroyOnClose
				maskClosable={!editSaving}
			>
				<div style={{ marginBottom: 8 }}>
					<strong>Message</strong>
				</div>
				<textarea
					style={{ width: '100%', minHeight: 120, marginBottom: 16 }}
					value={editMessage}
					onChange={e => setEditMessage(e.target.value)}
				/>
				<div style={{ marginBottom: 8 }}>
					<strong>Scheduled send time</strong>
					<div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 4 }}>
						Works for pending and approved. Saved as UTC; shown in ICT + ET below.
					</div>
				</div>
				<DatePicker
					showTime
					style={{ width: '100%' }}
					value={editScheduledAt}
					onChange={setEditScheduledAt}
					format="YYYY-MM-DD HH:mm"
					allowClear={false}
				/>
				{editScheduledAt && editScheduledAt.isValid() ? (
					<div style={{ marginTop: 10, fontSize: 13 }}>
						Preview: {formatDualClock(editScheduledAt.toDate())}
					</div>
				) : null}
			</Modal>
		</ListPageShell>
	)
}
